import { useState, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import { api } from "@/lib/api";
import { useQuickAdd } from "@/lib/quickAdd";
import { toast } from "sonner";
import { Icon } from "@/lib/icons";
import { Txt } from "@/components/Txt";
import { useAuth } from "@/context/AuthContext";
import { Button, Select } from "@/components/kit";

const CATS = [
  { id: "tools", label: "Tools" },
  { id: "plants", label: "Plants" },
  { id: "supplies", label: "Supplies" },
];

// Exact match to the New Event form's field rendering: full-width input, label block above.
const inputCls = "w-full bg-warm-cream border border-ink/15 rounded-lg px-3 py-2 min-h-[42px] text-[14px]";
const Field = ({ label, children }) => (
  <div><label className="text-[13px] font-semibold block mb-1">{label}</label>{children}</div>
);

function ManagerPicker({ ids, managers = [], onChange }) {
  const [q, setQ] = useState("");
  const [matches, setMatches] = useState([]);
  const [names, setNames] = useState({});
  useEffect(() => {
    if (managers.length) setNames((n) => ({ ...n, ...Object.fromEntries(managers.map((m) => [m.member_id, `${m.name} (${m.member_number})`])) }));
  }, [managers]);
  const search = async () => {
    const r = await api.get("/garden-plots/managers/search", { params: { q } });
    setMatches(r.data.matches);
  };
  const add = (m) => { if (!ids.includes(m.member_id)) { onChange([...ids, m.member_id]); setNames((n) => ({ ...n, [m.member_id]: `${m.name} (${m.member_number})` })); } setMatches([]); setQ(""); };
  return (
    <div data-testid="manager-picker">
      <div className="flex flex-wrap gap-1.5 mb-2">
        {ids.length === 0 && <span className="text-[13px] text-ink/45">No managers linked</span>}
        {ids.map((id) => (
          <span key={id} className="text-[13px] px-2 py-1 rounded bg-olive/20 border border-olive/40 flex items-center gap-1">
            {names[id] || id}
            <button data-testid={`remove-manager-${id}`} onClick={() => onChange(ids.filter((x) => x !== id))} className="text-maroon"><Icon name="X" className="w-3 h-3" /></button>
          </span>
        ))}
      </div>
      <div className="flex gap-2">
        <input data-testid="manager-search-input" placeholder="Find a gardener member…" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && search()} className="w-full bg-warm-cream border border-ink/15 rounded-lg px-3 py-2 min-h-[42px] text-[14px]" />
        <button data-testid="manager-search-btn" onClick={search} className="border border-ink/20 rounded-lg px-3 min-h-[42px] text-[14px] font-semibold">Search</button>
      </div>
      {matches.length > 0 && (
        <div className="mt-2 space-y-1">
          {matches.map((m) => (
            <button key={m.member_id} data-testid={`manager-match-${m.member_number}`} onClick={() => add(m)} className="w-full text-left text-[14px] px-3 py-2 rounded bg-olive/10 border border-olive/30 hover:bg-olive/20">{m.name} · {m.member_number}</button>
          ))}
        </div>
      )}
    </div>
  );
}

function PlotTasks({ plotId, tasks = [], managers = [], onChanged }) {
  const [desc, setDesc] = useState("");
  const [due, setDue] = useState("");
  const [assignees, setAssignees] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [edit, setEdit] = useState({ description: "", due_date: "", assignee_ids: [] });

  const toggleId = (list, id) => (list.includes(id) ? list.filter((x) => x !== id) : [...list, id]);

  const add = async () => {
    if (!desc.trim()) return toast.error("Enter a task description.");
    try {
      await api.post(`/garden-plots/${plotId}/tasks`, { description: desc, assignee_ids: assignees, due_date: due || null });
      setDesc(""); setDue(""); setAssignees([]); onChanged && onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not add task"); }
  };
  const toggleStatus = async (t) => {
    try { await api.put(`/garden-plots/${plotId}/tasks/${t.id}`, { status: t.status === "complete" ? "open" : "complete" }); onChanged && onChanged(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not update task"); }
  };
  const del = async (id) => { await api.delete(`/garden-plots/${plotId}/tasks/${id}`); onChanged && onChanged(); };
  const startEdit = (t) => { setEditingId(t.id); setEdit({ description: t.description, due_date: t.due_date || "", assignee_ids: t.assignee_ids || [] }); };
  const saveEdit = async () => {
    try {
      await api.put(`/garden-plots/${plotId}/tasks/${editingId}`, { description: edit.description, due_date: edit.due_date || null, assignee_ids: edit.assignee_ids });
      setEditingId(null); onChanged && onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save task"); }
  };

  return (
    <div className="bg-cream border border-ink/15 rounded-xl p-4" data-testid="plot-tasks">
      <h3 className="font-zilla text-lg font-semibold mb-1">Tasks</h3>
      <p className="text-[13px] text-ink/55 mb-3">Assign work to this plot's managers. Assignees see their open tasks on their own dashboard.</p>

      <div className="border border-ink/10 rounded-lg p-3 mb-3 space-y-2 bg-warm-cream" data-testid="task-add-form">
        <input data-testid="task-desc" placeholder="What needs doing?" value={desc} onChange={(e) => setDesc(e.target.value)} className={inputCls} />
        <div className="flex flex-wrap items-center gap-2">
          <label className="text-[13px] font-semibold">Due</label>
          <input data-testid="task-due" type="date" value={due} onChange={(e) => setDue(e.target.value)} className="bg-cream border border-ink/15 rounded-lg px-3 py-2 min-h-[42px] text-[14px]" />
        </div>
        <div>
          <div className="text-[13px] font-semibold mb-1">Assign to managers</div>
          {managers.length === 0 ? <div className="text-[13px] text-ink/45">Add a manager to this plot first to assign tasks.</div> : (
            <div className="flex flex-wrap gap-1.5" data-testid="task-assignees">
              {managers.map((m) => (
                <button key={m.member_id} data-testid={`task-assignee-${m.member_id}`} onClick={() => setAssignees((a) => toggleId(a, m.member_id))}
                  className={`text-[13px] px-2.5 py-1.5 rounded-lg border ${assignees.includes(m.member_id) ? "bg-olive/25 border-olive/50 font-semibold" : "bg-cream border-ink/15"}`}>
                  {m.name}
                </button>
              ))}
            </div>
          )}
        </div>
        <Button data-testid="task-add" onClick={add} className="px-4 min-h-[42px] text-[14px]">Add task</Button>
      </div>

      {tasks.length === 0 ? <div className="text-[14px] text-ink/50">No tasks yet.</div> : (
        <div className="space-y-1.5" data-testid="task-list">
          {tasks.map((t) => (
            <div key={t.id} className="border-b border-ink/8 last:border-0 py-2" data-testid={`task-row-${t.id}`}>
              <div className="flex items-start gap-2">
                <input type="checkbox" data-testid={`task-toggle-${t.id}`} checked={t.status === "complete"} onChange={() => toggleStatus(t)} className="w-4 h-4 mt-1 accent-olive" />
                <div className="flex-1 min-w-0">
                  <span className={`text-[15px] ${t.status === "complete" ? "line-through text-ink/40" : ""}`}>{t.description}</span>
                  <div className="text-[12px] text-ink/55 mt-0.5">
                    {(t.assignees || []).length ? t.assignees.map((a) => a.name).join(", ") : "Unassigned"}
                    {t.due_date ? ` · Due ${new Date(t.due_date + "T00:00:00").toLocaleDateString(undefined, { month: "short", day: "numeric" })}` : ""}
                  </div>
                </div>
                <button data-testid={`task-edit-${t.id}`} onClick={() => (editingId === t.id ? setEditingId(null) : startEdit(t))} className="text-ink/45 hover:text-ink"><Icon name="Pencil" className="w-4 h-4" /></button>
                <button data-testid={`task-del-${t.id}`} onClick={() => del(t.id)} className="text-ink/40 hover:text-maroon"><Icon name="X" className="w-4 h-4" /></button>
              </div>
              {editingId === t.id && (
                <div className="mt-2 ml-6 border border-ink/10 rounded-lg p-3 space-y-2 bg-warm-cream" data-testid={`task-edit-panel-${t.id}`}>
                  <input data-testid={`task-edit-desc-${t.id}`} value={edit.description} onChange={(e) => setEdit({ ...edit, description: e.target.value })} className={inputCls} />
                  <div className="flex flex-wrap items-center gap-2">
                    <label className="text-[13px] font-semibold">Due</label>
                    <input data-testid={`task-edit-due-${t.id}`} type="date" value={edit.due_date} onChange={(e) => setEdit({ ...edit, due_date: e.target.value })} className="bg-cream border border-ink/15 rounded-lg px-3 py-2 min-h-[42px] text-[14px]" />
                  </div>
                  {managers.length > 0 && (
                    <div className="flex flex-wrap gap-1.5">
                      {managers.map((m) => (
                        <button key={m.member_id} data-testid={`task-edit-assignee-${t.id}-${m.member_id}`} onClick={() => setEdit((e) => ({ ...e, assignee_ids: toggleId(e.assignee_ids, m.member_id) }))}
                          className={`text-[13px] px-2.5 py-1.5 rounded-lg border ${edit.assignee_ids.includes(m.member_id) ? "bg-olive/25 border-olive/50 font-semibold" : "bg-cream border-ink/15"}`}>
                          {m.name}
                        </button>
                      ))}
                    </div>
                  )}
                  <div className="flex gap-2">
                    <Button data-testid={`task-edit-save-${t.id}`} onClick={saveEdit} className="px-3 min-h-[38px] text-[13px]">Save</Button>
                    <Button variant="secondary" onClick={() => setEditingId(null)} className="px-3 min-h-[38px] text-[13px]">Cancel</Button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function PlotRecord({ plotId, onClose, onChanged, driveConnected }) {
  const [p, setP] = useState(null);
  const [need, setNeed] = useState({ category: "tools", text: "" });
  const [inv, setInv] = useState({ name: "", quantity: 1 });
  const [uploading, setUploading] = useState(false);
  const [tie, setTie] = useState(null);
  const load = useCallback(async () => { const r = await api.get(`/garden-plots/${plotId}`); setP(r.data); }, [plotId]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get(`/garden-plots/${plotId}/reimbursements`).then((r) => setTie(r.data)).catch(() => {}); }, [plotId, p]);
  if (!p) return null;

  const saveMeta = async (patch) => {
    try { await api.put(`/garden-plots/${plotId}`, patch); await load(); onChanged && onChanged(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not save changes"); await load(); }
  };
  const uploadFile = async (fileObj, kind) => {
    if (!fileObj) return;
    if (!driveConnected) return toast.error("Google Drive isn't connected yet. Ask a Super Admin to connect it.");
    const fd = new FormData();
    fd.append("file", fileObj);
    fd.append("kind", kind);
    setUploading(true);
    try {
      const r = await api.post(`/garden-plots/${plotId}/upload`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setP(r.data); onChanged && onChanged();
      toast.success(kind === "photo" ? "Photo uploaded" : "Document uploaded");
    } catch (e) { toast.error(e.response?.data?.detail || "Upload failed"); }
    finally { setUploading(false); }
  };
  const delDoc = async (id) => { await api.delete(`/garden-plots/${plotId}/documents/${id}`); await load(); onChanged && onChanged(); };
  const addNeed = async () => { if (!need.text.trim()) return; await api.post(`/garden-plots/${plotId}/needs`, need); setNeed({ category: need.category, text: "" }); await load(); onChanged && onChanged(); };
  const toggleNeed = async (id) => { await api.put(`/garden-plots/${plotId}/needs/${id}/toggle`); await load(); onChanged && onChanged(); };
  const delNeed = async (id) => { await api.delete(`/garden-plots/${plotId}/needs/${id}`); await load(); onChanged && onChanged(); };
  const addInv = async () => { if (!inv.name.trim()) return; await api.post(`/garden-plots/${plotId}/inventory`, inv); setInv({ name: "", quantity: 1 }); await load(); };
  const setQty = async (id, quantity) => { await api.put(`/garden-plots/${plotId}/inventory/${id}`, { quantity }); await load(); };
  const delInv = async (id) => { await api.delete(`/garden-plots/${plotId}/inventory/${id}`); await load(); };
  const archive = async () => {
    const to = p.status === "archived" ? "unarchive" : "archive";
    try { await api.post(`/garden-plots/${plotId}/${to}`); toast.success(to === "archive" ? "Plot archived" : "Plot restored"); onChanged && onChanged(); onClose(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not update plot"); }
  };

  return (
    <div data-testid="plot-record">
      <button onClick={onClose} data-testid="plot-record-close" className="inline-flex items-center gap-1.5 text-[14px] font-semibold text-ink/70 hover:text-ink mb-3">
        <Icon name="ChevronLeft" className="w-4 h-4" /> Back to garden plots
      </button>
      <div className="mb-4">
        <div className="text-[12px] uppercase tracking-widest text-maroon font-bold">100th Street Plot</div>
        <h1 className="font-zilla text-3xl sm:text-4xl font-semibold">{p.location}</h1>
      </div>
      <div className="max-w-3xl space-y-5">
          {/* Location + theme + managers — full-width fields matching New Event */}
          <div className="space-y-3">
            <Field label="Cross-street location (100th St)">
              <input data-testid="plot-location" defaultValue={p.location} onBlur={(e) => saveMeta({ location: e.target.value })} className={inputCls} />
            </Field>
            <Field label="Theme">
              <input data-testid="plot-theme" defaultValue={p.theme} onBlur={(e) => saveMeta({ theme: e.target.value })} className={inputCls} />
            </Field>
            <div>
              <div className="text-[13px] font-semibold block mb-1">Managers (linked gardener members)</div>
              <ManagerPicker ids={p.manager_ids} managers={p.managers} onChange={(ids) => saveMeta({ manager_ids: ids })} />
            </div>
          </div>

          {/* Photo */}
          <div className="bg-cream border border-ink/15 rounded-xl p-4">
            <h3 className="font-zilla text-lg font-semibold mb-2">Current photo</h3>
            {p.photo_url ? <img src={p.photo_url} alt="" data-testid="plot-photo" className="w-full max-h-56 object-contain bg-warm-cream rounded-lg border border-ink/15 mb-2" /> : <div className="text-[14px] text-ink/50 mb-2">No photo yet.</div>}
            <label data-testid="plot-photo-upload-label" className={`inline-flex items-center gap-2 rounded-lg px-4 min-h-[42px] text-[14px] font-semibold ${driveConnected && !uploading ? "bg-ink text-cream cursor-pointer" : "bg-ink/20 text-ink/50 cursor-not-allowed"}`}>
              <Icon name="Upload" className="w-4 h-4" />
              {uploading ? "Uploading…" : <Txt id="gardens.plots.photo_replace" screen="Garden Plots">Upload a new photo</Txt>}
              <input data-testid="plot-photo-input" type="file" accept="image/*" disabled={!driveConnected || uploading} onChange={(e) => { uploadFile(e.target.files[0], "photo"); e.target.value = ""; }} className="hidden" />
            </label>
            {!driveConnected && <p className="text-[12px] text-maroon mt-1" data-testid="plot-drive-warning">Google Drive isn't connected yet.</p>}
            {(p.photo_history || []).length > 0 && (
              <div className="mt-3">
                <div className="text-[12px] uppercase tracking-wide text-ink/50 font-bold mb-1">Prior photos</div>
                <div className="flex gap-2 flex-wrap" data-testid="plot-photo-history">
                  {p.photo_history.map((h, i) => <img key={i} src={h.url} alt="" className="w-16 h-16 object-contain bg-warm-cream rounded border border-ink/15" />)}
                </div>
              </div>
            )}
          </div>

          {/* Documents */}
          <div className="bg-cream border border-ink/15 rounded-xl p-4" data-testid="plot-documents">
            <h3 className="font-zilla text-lg font-semibold mb-2"><Txt id="gardens.plots.docs_title" screen="Garden Plots">Documents</Txt></h3>
            <label data-testid="plot-doc-upload-label" className={`inline-flex items-center gap-2 rounded-lg px-4 min-h-[42px] text-[14px] font-semibold mb-3 ${driveConnected && !uploading ? "bg-ink text-cream cursor-pointer" : "bg-ink/20 text-ink/50 cursor-not-allowed"}`}>
              <Icon name="Upload" className="w-4 h-4" />
              {uploading ? "Uploading…" : <Txt id="gardens.plots.docs_upload" screen="Garden Plots">Upload a document</Txt>}
              <input data-testid="plot-doc-input" type="file" disabled={!driveConnected || uploading} onChange={(e) => { uploadFile(e.target.files[0], "document"); e.target.value = ""; }} className="hidden" />
            </label>
            {(p.documents || []).length === 0 ? <div className="text-[14px] text-ink/50">No documents yet.</div> : (
              <div className="space-y-1" data-testid="plot-doc-list">
                {p.documents.map((d) => (
                  <div key={d.id} className="flex items-center gap-2 py-1.5 border-b border-ink/8 last:border-0" data-testid={`doc-row-${d.id}`}>
                    <Icon name="FileText" className="w-4 h-4 text-olive" />
                    <a href={d.url} target="_blank" rel="noreferrer" className="flex-1 text-[15px] text-dark-olive underline truncate">{d.name}</a>
                    <button data-testid={`doc-del-${d.id}`} onClick={() => delDoc(d.id)} className="text-ink/40 hover:text-maroon"><Icon name="X" className="w-4 h-4" /></button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Needs checklist */}
          <div className="bg-cream border border-ink/15 rounded-xl p-4">
            <h3 className="font-zilla text-lg font-semibold mb-2">Needs checklist</h3>
            <div className="flex gap-2 mb-3">
              <Select data-testid="need-category" value={need.category} onChange={(e) => setNeed({ ...need, category: e.target.value })} className="bg-warm-cream border border-ink/15 rounded-lg px-2 py-2 min-h-[42px] text-[14px]">
                {CATS.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
              </Select>
              <input data-testid="need-text" placeholder="Add a need…" value={need.text} onChange={(e) => setNeed({ ...need, text: e.target.value })} onKeyDown={(e) => e.key === "Enter" && addNeed()} className="flex-1 bg-warm-cream border border-ink/15 rounded-lg px-3 py-2 min-h-[42px] text-[14px]" />
              <Button data-testid="need-add" onClick={addNeed} className="px-4 min-h-[42px] text-[14px]">Add</Button>
            </div>
            {CATS.map((c) => {
              const items = (p.needs || []).filter((n) => n.category === c.id);
              if (items.length === 0) return null;
              return (
                <div key={c.id} className="mb-2" data-testid={`needs-${c.id}`}>
                  <div className="text-[12px] uppercase tracking-wide text-ink/50 font-bold">{c.label}</div>
                  {items.map((n) => (
                    <div key={n.id} className="flex items-center gap-2 py-1.5 border-b border-ink/8 last:border-0">
                      <input type="checkbox" data-testid={`need-toggle-${n.id}`} checked={n.fulfilled} onChange={() => toggleNeed(n.id)} className="w-4 h-4 accent-olive" />
                      <span className={`flex-1 text-[15px] ${n.fulfilled ? "line-through text-ink/40" : ""}`}>{n.text}</span>
                      <button data-testid={`need-del-${n.id}`} onClick={() => delNeed(n.id)} className="text-ink/40 hover:text-maroon"><Icon name="X" className="w-4 h-4" /></button>
                    </div>
                  ))}
                </div>
              );
            })}
          </div>

          {/* Tasks */}
          <PlotTasks plotId={plotId} tasks={p.tasks} managers={p.managers} onChanged={load} />

          {/* On-site inventory */}
          <div className="bg-cream border border-ink/15 rounded-xl p-4">
            <h3 className="font-zilla text-lg font-semibold mb-1">On-site inventory</h3>
            <p className="text-[13px] text-ink/55 mb-3" data-testid="inventory-note">What's physically at this plot right now. This is <strong>separate from the system's Merchandise Inventory</strong> — items here are not merch stock.</p>
            <div className="flex gap-2 mb-3">
              <input data-testid="inv-name" placeholder="Item" value={inv.name} onChange={(e) => setInv({ ...inv, name: e.target.value })} className="flex-1 bg-warm-cream border border-ink/15 rounded-lg px-3 py-2 min-h-[42px] text-[14px]" />
              <input data-testid="inv-qty" type="number" min="0" value={inv.quantity} onChange={(e) => setInv({ ...inv, quantity: parseInt(e.target.value || "0", 10) })} className="w-20 bg-warm-cream border border-ink/15 rounded-lg px-3 py-2 min-h-[42px] text-[14px]" />
              <Button data-testid="inv-add" onClick={addInv} className="px-4 min-h-[42px] text-[14px]">Add</Button>
            </div>
            {(p.inventory || []).map((it) => (
              <div key={it.id} className="flex items-center gap-2 py-1.5 border-b border-ink/8 last:border-0" data-testid={`inv-row-${it.id}`}>
                <span className="flex-1 text-[15px]">{it.name}</span>
                <input type="number" min="0" data-testid={`inv-qty-${it.id}`} defaultValue={it.quantity} onBlur={(e) => setQty(it.id, parseInt(e.target.value || "0", 10))} className="w-20 bg-warm-cream border border-ink/15 rounded-lg px-2 py-1.5 min-h-[36px] text-[14px]" />
                <button data-testid={`inv-del-${it.id}`} onClick={() => delInv(it.id)} className="text-ink/40 hover:text-maroon"><Icon name="X" className="w-4 h-4" /></button>
              </div>
            ))}
          </div>
          {/* Reimbursements & hours tie-in */}
          <div className="bg-cream border border-ink/15 rounded-xl p-4" data-testid="plot-reimbursements">
            <h3 className="font-zilla text-lg font-semibold mb-1">Spending & hours for this plot</h3>
            <p className="text-[13px] text-ink/55 mb-3">Linked from the Reimbursements queue and the Garden Hours log.</p>
            <div className="flex gap-4 mb-3 flex-wrap">
              <div><div className="font-zilla text-2xl font-semibold">{tie?.hours_total ?? 0}</div><div className="text-[11px] uppercase tracking-wide text-ink/50">volunteer hours</div></div>
              <div><div className="font-zilla text-2xl font-semibold">{(tie?.reimbursements || []).length}</div><div className="text-[11px] uppercase tracking-wide text-ink/50">reimbursements</div></div>
              <div><div className="font-zilla text-2xl font-semibold">{(tie?.in_kind || []).length}</div><div className="text-[11px] uppercase tracking-wide text-ink/50">in-kind logged</div></div>
            </div>
            {(tie?.reimbursements || []).length === 0 ? <p className="text-[13px] text-ink/50">No reimbursements linked to this plot.</p>
              : (tie.reimbursements.map((r) => (
                <div key={r.reimb_id} className="flex items-center justify-between gap-2 py-1.5 border-b border-ink/8 last:border-0 text-[14px]" data-testid={`plot-reimb-${r.reimb_id}`}>
                  <span className="truncate">${((r.amount_cents || 0) / 100).toFixed(2)} · {r.description}</span>
                  <span className="text-[12px] px-2 py-0.5 rounded-full bg-ink/8 font-semibold capitalize shrink-0">{r.status}</span>
                </div>
              )))}
          </div>

          {/* Archive / restore */}
          <div className="bg-cream border border-maroon/30 rounded-xl p-4">
            <h3 className="font-zilla text-lg font-semibold mb-1">{p.status === "archived" ? "Archived plot" : "Archive this plot"}</h3>
            <p className="text-[13px] text-ink/55 mb-3">Archiving hides the plot from the active directory and summary but keeps its full record. You can restore it any time.</p>
            <button data-testid="archive-plot-btn" onClick={archive} className={`rounded-lg px-4 py-2 min-h-[42px] text-[14px] font-semibold flex items-center gap-1.5 ${p.status === "archived" ? "border border-olive/50" : "bg-maroon text-cream"}`}>
              <Icon name={p.status === "archived" ? "RotateCcw" : "Trash2"} className="w-4 h-4" /> {p.status === "archived" ? "Restore to active" : "Archive plot"}
            </button>
          </div>
        </div>
      </div>
  );
}

export default function GardenPlots() {
  const { me } = useAuth();
  const [data, setData] = useState({ plots: [], total: 0 });
  const [skip, setSkip] = useState(0);
  const [q, setQ] = useState("");
  const [showArchived, setShowArchived] = useState(false);
  const [openId, setOpenId] = useState(null);
  const [creating, setCreating] = useState(false);
  useQuickAdd("garden_plots", () => setCreating(true));
  const [newPlot, setNewPlot] = useState({ location: "", theme: "" });
  const [drive, setDrive] = useState({ connected: false, account_email: "" });
  const limit = 20;

  const load = useCallback(async () => {
    const r = await api.get("/garden-plots", { params: { limit, skip, ...(q ? { q } : {}), ...(showArchived ? { status: "archived" } : {}) } });
    setData(r.data);
  }, [skip, q, showArchived]);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const pid = sessionStorage.getItem("rca_open_plot");
    if (pid) { sessionStorage.removeItem("rca_open_plot"); setOpenId(pid); }
  }, []);

  const loadDrive = useCallback(async () => { try { const r = await api.get("/drive/status"); setDrive(r.data); } catch (e) {} }, []);
  useEffect(() => { loadDrive(); }, [loadDrive]);

  const create = async () => {
    if (!newPlot.location.trim()) return toast.error("Enter a cross-street location.");
    const r = await api.post("/garden-plots", { location: newPlot.location, theme: newPlot.theme, manager_ids: [] });
    setCreating(false); setNewPlot({ location: "", theme: "" }); await load(); setOpenId(r.data.plot_id);
  };

  if (openId && data.can_manage) return <PlotRecord plotId={openId} onClose={() => setOpenId(null)} onChanged={load} driveConnected={drive.connected} />;
  const canManage = data.can_manage;

  return (
    <div data-testid="garden-plots-page">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-1">
        <h1 className="font-zilla text-3xl sm:text-4xl font-semibold"><Txt id="gardens.plots.title" screen="Garden Plots">Garden Plots</Txt></h1>
        {canManage && <Button data-testid="add-plot-btn" onClick={() => setCreating(true)}><Icon name="Plus" className="w-4 h-4" /> <Txt id="gardens.plots.add" screen="Garden Plots">Add plot</Txt></Button>}
      </div>
      <p className="text-[16px] text-ink/70 mb-5">{canManage
        ? <Txt id="gardens.plots.subtitle" screen="Garden Plots">Every 100th Street plot — theme, managers, current photo, and outstanding needs at a glance.</Txt>
        : <Txt id="gardens.plots.subtitle_view" screen="Garden Plots">A look at every 100th Street garden plot.</Txt>}</p>

      {canManage && !drive.connected && (
        <div className="bg-gold/15 border border-gold/40 rounded-lg p-4 mb-4" data-testid="drive-connect-banner">
          <span className="text-[15px] text-ink/80"><Txt id="gardens.plots.drive_banner" screen="Garden Plots">Photo and document uploads use the association's shared Google Drive. A Super Admin can connect it under Settings → Connections.</Txt></span>
        </div>
      )}
      {canManage && drive.connected && (
        <div className="text-[13px] text-dark-olive mb-4 flex items-center gap-1.5" data-testid="drive-connected-note"><Icon name="CheckCircle2" className="w-4 h-4" /> Google Drive connected{drive.account_email ? ` · ${drive.account_email}` : ""}</div>
      )}

      {canManage && (
        <div className="flex items-center gap-3 flex-wrap mb-4">
          <input data-testid="plot-search" placeholder="Search location or theme…" value={q} onChange={(e) => { setSkip(0); setQ(e.target.value); }} className="flex-1 min-w-[220px] max-w-md bg-warm-cream border border-ink/15 rounded-lg px-3 py-2.5 min-h-[44px] text-[15px]" />
          <label className="flex items-center gap-2 text-[14px] font-semibold" data-testid="show-archived-toggle">
            <input type="checkbox" checked={showArchived} onChange={(e) => { setSkip(0); setShowArchived(e.target.checked); }} className="w-5 h-5 accent-maroon" />
            Show archived
          </label>
        </div>
      )}

      <div className="grid gap-4" style={{ gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))" }} data-testid="plots-grid">
        {data.plots.length === 0 && <div className="text-ink/55 text-[16px] py-10">No garden plots yet.</div>}
        {canManage ? data.plots.map((p) => (
          <button key={p.plot_id} data-testid={`plot-card-${p.plot_id}`} onClick={() => setOpenId(p.plot_id)} className="text-left bg-cream border border-ink/15 rounded-xl overflow-hidden hover:-translate-y-0.5 transition-transform">
            {p.photo_url ? <img src={p.photo_url} alt="" className="w-full h-40 object-contain bg-warm-cream" /> : <div className="w-full h-40 bg-olive/15 flex items-center justify-center"><Icon name="Sprout" className="w-10 h-10 text-olive" /></div>}
            <div className="p-4">
              <div className="font-zilla text-lg font-semibold">{p.location}</div>
              <div className="text-[14px] text-ink/70">{p.theme || "No theme set"}</div>
              <div className="text-[13px] text-ink/60 mt-1">{p.managers.length ? p.managers.map((m) => m.name).join(", ") : "No managers"}</div>
              <div className="mt-2">
                <div className="text-[12px] uppercase tracking-wide text-ink/45 font-bold">Outstanding needs</div>
                {p.needs_outstanding.length === 0 ? <span className="text-[13px] text-dark-olive">All caught up</span> : (
                  <div className="flex flex-wrap gap-1 mt-1">
                    {p.needs_outstanding.slice(0, 5).map((n, i) => <span key={i} className="text-[12px] px-2 py-0.5 rounded bg-gold/20 border border-gold/40 capitalize">{n.text}</span>)}
                    {p.needs_outstanding.length > 5 && <span className="text-[12px] text-ink/50">+{p.needs_outstanding.length - 5}</span>}
                  </div>
                )}
              </div>
            </div>
          </button>
        )) : data.plots.map((p) => (
          <div key={p.plot_id} data-testid={`plot-card-readonly-${p.plot_id}`} className="bg-cream border border-ink/15 rounded-xl overflow-hidden">
            {p.photo_url ? <img src={p.photo_url} alt="" className="w-full h-40 object-contain bg-warm-cream" /> : <div className="w-full h-40 bg-olive/15 flex items-center justify-center"><Icon name="Sprout" className="w-10 h-10 text-olive" /></div>}
            <div className="p-4">
              <div className="font-zilla text-lg font-semibold">{p.location}</div>
              <div className="text-[14px] text-ink/70">{p.theme || "No theme set"}</div>
              <div className="text-[13px] text-ink/60 mt-1">{(p.managers || []).length ? p.managers.map((m) => m.name).join(", ") : "No owners listed"}</div>
              <div className="mt-2">
                <div className="text-[12px] uppercase tracking-wide text-ink/45 font-bold">Outstanding needs</div>
                {(p.needs_outstanding || []).length === 0 ? <span className="text-[13px] text-dark-olive">All caught up</span> : (
                  <div className="flex flex-wrap gap-1 mt-1">
                    {p.needs_outstanding.slice(0, 5).map((n, i) => <span key={i} className="text-[12px] px-2 py-0.5 rounded bg-gold/20 border border-gold/40 capitalize">{n.text}</span>)}
                    {p.needs_outstanding.length > 5 && <span className="text-[12px] text-ink/50">+{p.needs_outstanding.length - 5}</span>}
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="flex items-center justify-between mt-4">
        <span className="text-[14px] text-ink/60">Showing {data.plots.length ? skip + 1 : 0}–{skip + data.plots.length} of {data.total}</span>
        <div className="flex gap-2">
          <Button variant="secondary" data-testid="plots-prev" disabled={skip === 0} onClick={() => setSkip(Math.max(0, skip - limit))}>Previous</Button>
          <Button variant="secondary" data-testid="plots-next" disabled={skip + limit >= data.total} onClick={() => setSkip(skip + limit)}>Next</Button>
        </div>
      </div>

      {canManage && creating && (
        <div className="fixed inset-0 z-50 bg-ink/50 flex items-center justify-center p-4" onClick={() => setCreating(false)}>
          <div className="bg-warm-cream rounded-xl p-6 w-full max-w-md" onClick={(e) => e.stopPropagation()} data-testid="create-plot-modal">
            <h2 className="font-zilla text-xl font-semibold mb-3">New garden plot</h2>
            <div className="space-y-3 mb-3">
              <Field label="Cross-street location"><input data-testid="new-plot-location" placeholder="e.g. 100th & Green" value={newPlot.location} onChange={(e) => setNewPlot({ ...newPlot, location: e.target.value })} className={inputCls} /></Field>
              <Field label="Theme (optional)"><input data-testid="new-plot-theme" placeholder="Theme" value={newPlot.theme} onChange={(e) => setNewPlot({ ...newPlot, theme: e.target.value })} className={inputCls} /></Field>
            </div>
            <div className="flex gap-2">
              <Button data-testid="create-plot-submit" onClick={create}>Create plot</Button>
              <Button variant="secondary" onClick={() => setCreating(false)}>Cancel</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
